import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  Check as CheckIcon,
  Language as LanguageIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useCurrency } from './CurrencyContext';
import { useTranslation } from 'react-i18next';

export default function CurrencySelector({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currencies, selectedCurrency, changeCurrency, loading } = useCurrency();
  const [tempCurrency, setTempCurrency] = useState(selectedCurrency);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTempCurrency(selectedCurrency);
    }
  }, [open, selectedCurrency]);

  const handleSave = async () => {
    if (tempCurrency === selectedCurrency) {
      onClose();
      return;
    }

    setSaving(true);
    const success = await changeCurrency(tempCurrency);
    setSaving(false);
    
    if (success) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // ✅ Full screen on mobile
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          paddingTop: isMobile ? 0 : 7,
          boxShadow: isMobile ? 0 : '0 8px 32px rgba(0,0,0,0.1)',
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '60vh' : '90vh'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          <Typography 
            variant="h6" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            {t('selectCurrency') || 'Select Currency'}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          size={isMobile ? "small" : "medium"}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            '&:active': { transform: 'scale(0.95)' }
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert 
          severity="info" 
          icon={<LanguageIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />} 
          sx={{ 
            mb: { xs: 2, sm: 3 }, 
            borderRadius: 2,
            fontSize: { xs: '0.85rem', sm: '0.95rem' }
          }}
        >
          <Typography 
            variant="body2"
            sx={{ fontSize: 'inherit' }}
          >
            {t('currencySelectorInfo') || 'Choose your preferred currency. Prices will be displayed in your selected currency throughout the app.'}
          </Typography>
        </Alert>

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: { xs: 4, sm: 6 }, 
            gap: 2 
          }}>
            <CircularProgress size={isMobile ? 40 : 48} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              {t('loadingCurrencies') || 'Loading currencies...'}
            </Typography>
          </Box>
        ) : currencies.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: { xs: 4, sm: 6 }, 
            gap: 2 
          }}>
            <InfoIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: 'text.disabled' }} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              {t('noCurrenciesAvailable') || 'No currencies available'}
            </Typography>
          </Box>
        ) : (
          <RadioGroup 
            value={tempCurrency} 
            onChange={(e) => setTempCurrency(e.target.value)}
          >
            {currencies.map((currency) => (
              <Paper
                key={currency.code}
                elevation={tempCurrency === currency.code ? 3 : 0}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  p: { xs: 1.5, sm: 2 },
                  border: '2px solid',
                  borderColor: tempCurrency === currency.code ? 'primary.main' : 'divider',
                  borderRadius: { xs: 2, sm: 2.5 },
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  bgcolor: tempCurrency === currency.code ? '#f0f7ff' : 'background.paper',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2
                  },
                  '&:active': {
                    transform: isMobile ? 'scale(0.98)' : 'none'
                  }
                }}
                onClick={() => setTempCurrency(currency.code)}
              >
                <FormControlLabel
                  value={currency.code}
                  control={
                    <Radio 
                      color="primary" 
                      size={isMobile ? "small" : "medium"}
                    />
                  }
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      width: '100%', 
                      ml: { xs: 0.5, sm: 1 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.15rem' },
                            flexWrap: 'wrap'
                          }}
                        >
                          {currency.symbol} {currency.code}
                          {currency.code === selectedCurrency && (
                            <Chip 
                              label={t('current') || 'Current'} 
                              size="small" 
                              color="success"
                              icon={<CheckIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                              sx={{ 
                                fontWeight: 'bold',
                                height: { xs: 22, sm: 24 },
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            />
                          )}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {currency.name}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${currency.decimals} ${t('decimals') || 'decimals'}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          height: { xs: 22, sm: 24 },
                          alignSelf: { xs: 'flex-start', sm: 'center' }
                        }}
                      />
                    </Box>
                  }
                  sx={{ 
                    width: '100%',
                    m: 0,
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    '& .MuiFormControlLabel-label': {
                      width: '100%'
                    }
                  }}
                />
              </Paper>
            ))}
          </RadioGroup>
        )}

       
      </DialogContent>
       <Divider sx={{ my: { xs: 2, sm: 3 } }} />

        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={saving}
            size={isMobile ? "large" : "large"}
            sx={{ 
              fontWeight: 'bold',
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={saving || tempCurrency === selectedCurrency}
            size={isMobile ? "large" : "large"}
            startIcon={
              saving ? (
                <CircularProgress 
                  size={isMobile ? 18 : 20} 
                  color="inherit" 
                />
              ) : (
                <CheckIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
              )
            }
            sx={{ 
              fontWeight: 'bold',
              bgcolor: 'primary.main',
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
          >
            {saving ? (t('saving') || 'Saving...') : (t('save') || 'Save')}
          </Button>
        </Box>
    </Dialog>
  );
}
